import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import UmlautHelper from './UmlautHelper';
import UmlautButtons from './UmlautButtons';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const QuestionAndAnswer = ({ question, onAnswer, onNext, sessionData = null, langCode = null }) => {
  const [answers, setAnswers] = useState({});
  const [activeAudioUrl, setActiveAudioUrl] = useState(null);
  const [autoPlayRequested, setAutoPlayRequested] = useState(false);
  const activeAnnotationRef = useRef(null);
  const textareaRefs = useRef({});
  const submitButtonRef = useRef(null);

  // Transform backend data to component format
  // Get readingText from multiple sources and keep the largest one
  const questionText = question.text || '';
  const questionPassage = question.passage || '';
  const questionReadingText = question['reading-text'] || '';
  const questionContext = question.context || '';
  const sessionDataPassage = sessionData?.passage || '';
  let storagePassage = '';
  
  // Try to get passage from localStorage
  try {
    const qpSessionRaw = localStorage.getItem("qp-session");
    if (qpSessionRaw) {
      const parsed = JSON.parse(qpSessionRaw);
      storagePassage = parsed.passage || '';
    } else {
      const qpPracticeRaw = localStorage.getItem("qp-practice");
      if (qpPracticeRaw) {
        const parsed = JSON.parse(qpPracticeRaw);
        storagePassage = parsed.passage || '';
      }
    }
  } catch (error) {
    // Ignore localStorage errors
  }
  
  // Keep the largest readingText from all sources (including sessionData)
  const allSources = [questionText, questionPassage, questionReadingText, questionContext, sessionDataPassage, storagePassage];
  const readingText = allSources.reduce((largest, current) => 
    (current && current.length > (largest || '').length) ? current : largest, '');
  
  // Handle questions - can be array of strings or array of objects
  const questions = question.questions || question['sub-questions'] || [];
  
  // Transform questions array - handle both string array and object array
  const questionsArray = useMemo(() => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return [];
    }
    
    return questions.map((q, index) => {
      // If q is a string, return it directly
      if (typeof q === 'string') {
        return q;
      }
      // If q is an object, extract text
      return q.text || q.question || q['text-soal'] || '';
    }).filter(q => q && q.trim().length > 0);
  }, [questions]);

  // Reset answers when question changes
  useEffect(() => {
    setAnswers({});
    setActiveAudioUrl(null);
    setAutoPlayRequested(false);
    // Reset textarea heights
    Object.values(textareaRefs.current).forEach(textarea => {
      if (textarea) {
        textarea.style.height = 'auto';
      }
    });
  }, [question['soal-id'] || question.id]);

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      console.log('🎵 QuestionAndAnswer (Practice) - Raw annotations:', question.annotations);
      
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        console.log('🎵 QuestionAndAnswer (Practice) - Processing annotation:', {
          annId,
          streamUrl,
          annType,
          rawAnnotation: annotation
        });
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    if (readingText && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(readingText, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            console.log('🎵 QuestionAndAnswer (Practice) - Fallback annotation from HTML:', {
              annId,
              annPath,
              baseURL,
              streamUrl
            });
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        console.warn('QuestionAndAnswer: failed to parse annotations from HTML', error);
      }
    }

    console.log('🎵 QuestionAndAnswer (Practice) - Final annotationsMap:', map);
    return map;
  }, [question.annotations, readingText]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  // changed by Cursor: hanya promote annotation passage ke waveform global; annotation per-question di-render sebagai inline player
  const firstAnnotationAudioUrl = useMemo(() => {
    const tryParse = (html) => {
      if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
      try {
        const doc = new window.DOMParser().parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');
        for (const node of nodes) {
          const annId = node.getAttribute('data-ann-id');
          if (annId && annotationsMap[annId]?.streamUrl) {
            return annotationsMap[annId].streamUrl;
          }
        }
      } catch {}
      return null;
    };
    return tryParse(readingText);
  }, [readingText, annotationsMap]);

  // changed by Cursor: per-question audio URL dari annotation di tiap question HTML — buat inline player
  const perQuestionAudioUrl = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return questionsArray.map(() => null);
    }
    return questionsArray.map((html) => {
      if (!html) return null;
      try {
        const doc = new window.DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[data-ann-id]');
        if (!node) return null;
        const annId = node.getAttribute('data-ann-id');
        const audio = annotationsMap[annId];
        if (audio?.streamUrl && (audio.annType === 'audio' || !audio.annType)) {
          return audio.streamUrl;
        }
      } catch {}
      return null;
    });
  }, [questionsArray, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl || activeAudioUrl;

  // Helper function to enhance HTML text with annotation support; hide annotation shown via waveform
  const enhanceHtmlWithAnnotations = (htmlText) => {
    if (!htmlText || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return htmlText;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            if (!node.classList.contains('practice-annotation')) {
              node.classList.add('practice-annotation');
            }
            if (!node.classList.contains('annotation-hidden')) {
              node.classList.add('annotation-hidden');
            }
            if (!node.classList.contains('annotation')) {
              node.classList.add('annotation');
            }
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            if (!node.hasAttribute('aria-label')) node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('QuestionAndAnswer: failed to enhance HTML', error);
      return htmlText;
    }
  };

  // Enhance HTML with annotation classes for reading text
  const enhancedReadingText = useMemo(() => {
    return enhanceHtmlWithAnnotations(readingText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingText, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  // Enhance questions array with annotation support
  const enhancedQuestionsArray = useMemo(() => {
    return questionsArray.map((questionText) => {
      return enhanceHtmlWithAnnotations(questionText);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsArray, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const handlePlayAnnotation = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;
    setActiveAudioUrl(streamUrl);
    setAutoPlayRequested(true);
  };

  const handleAudioEnded = () => {
    if (activeAnnotationRef.current) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
      activeAnnotationRef.current = null;
    }
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    // Don't handle if clicking on textarea
    if (event.target.closest('textarea')) {
      return;
    }

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers(prev => ({ ...prev, [questionIndex]: value }));
    // Auto-resize after state update
    setTimeout(() => {
      const textarea = textareaRefs.current[questionIndex];
      if (textarea) {
        autoResizeTextarea(textarea);
      }
    }, 0);
  };

  const handleSubmit = () => {
    // Check if all questions are answered
    const allAnswered = questionsArray.every((_, index) => {
      return answers[index] && answers[index].trim().length > 0;
    });

    if (!allAnswered) return;

    // Convert object answers to array format for backend
    const answersArray = questionsArray.map((_, index) => answers[index] || '');

    if (onAnswer) {
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: answersArray, // Send as array instead of object
        isCorrect: false // Will be determined by backend
      });
    }
    if (onNext) onNext();
  };

  const allAnswered = questionsArray.every((_, index) => {
    return answers[index] && answers[index].trim().length > 0;
  });

  const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

  const textareaCls =
    'w-full p-4 pr-[60px] max-md:pr-[50px] max-md:p-3.5 border-2 border-[rgba(18,118,109,0.2)] dark:border-[rgba(20,184,166,0.25)] rounded-xl text-base text-[var(--text-primary)] bg-[var(--card-bg)] dark:bg-[var(--bg-secondary)] resize-none min-h-[44px] max-h-[200px] max-md:max-h-none overflow-y-hidden transition-all duration-300 leading-relaxed font-[inherit] focus:outline-none focus:border-[#12766d] focus:shadow-[0_0_0_3px_rgba(18,118,109,0.1)] dark:focus:border-[#14b8a6] dark:focus:shadow-[0_0_0_3px_rgba(20,184,166,0.15)] placeholder:opacity-50 placeholder:text-[rgba(26,26,46,0.4)] dark:placeholder:text-[var(--text-tertiary)] max-md:text-base';

  return (
    <motion.div
      className="flex flex-col gap-5 max-md:gap-4 max-md:pb-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:flex-col max-md:items-start max-md:gap-3">
        <motion.span
          className="px-3.5 py-2 bg-gradient-to-br from-[#12766d] to-[#236463] text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Question and Answer
        </motion.span>
      </div>

      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer
            audioUrl={displayAudioUrl}
            autoPlay={autoPlayRequested}
            onPlayStarted={() => setAutoPlayRequested(false)}
            onEnded={handleAudioEnded}
          />
        )}
        {readingText && (
          <div className="mb-6 p-5 bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.08)] rounded-xl border-l-4 border-[#12766d] dark:border-[#14b8a6] max-md:p-4 max-md:mb-5">
            <div
              className="passage-text text-base leading-relaxed text-[var(--text-primary)] whitespace-pre-line"
              onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
              onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
              dangerouslySetInnerHTML={{ __html: enhancedReadingText }}
            />
          </div>
        )}

        <div className="flex flex-col gap-4 max-md:gap-0.5 mt-3 max-md:mt-1">
          {questionsArray.map((questionText, index) => {
            const enhancedQuestionText = enhancedQuestionsArray[index] || questionText;
            const hasQuestionAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;
            // changed by Cursor: kalau question punya audio annotation → render inline player + label, sembunyiin teks (teks = jawaban)
            const inlineAudioUrl = perQuestionAudioUrl[index];

            const insertCharIntoTextarea = (char) => {
              const textarea = textareaRefs.current[index];
              if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = answers[index] || '';
                const newValue = currentValue.substring(0, start) + char + currentValue.substring(end);
                handleAnswerChange(index, newValue);
                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start + char.length, start + char.length);
                }, 0);
              }
            };

            return (
              <div key={index} className="flex flex-col gap-2 max-md:gap-0.5">
                {inlineAudioUrl ? (
                  <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)] shrink-0 max-md:basis-full">
                      Question {index + 1}
                    </span>
                    <div className="flex-1 min-w-0 max-md:basis-full">
                      <QuestionAudioPlayer
                        audioUrl={inlineAudioUrl}
                        showQuota={false}
                        fullWidth
                      />
                    </div>
                    <div className="shrink-0 max-md:basis-full max-md:flex max-md:justify-end">
                      <UmlautButtons
                        langCode={langCode}
                        onInsertChar={insertCharIntoTextarea}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap max-md:flex-col max-md:items-start max-md:gap-0.5">
                    <div
                      className="question-text flex-1 min-w-0 text-base font-semibold text-[var(--text-primary)] leading-relaxed m-0 touch-manipulation"
                      onClick={hasQuestionAnnotations ? handleAnnotationTrigger : undefined}
                      onKeyDown={hasQuestionAnnotations ? handleAnnotationKeyDown : undefined}
                      dangerouslySetInnerHTML={{ __html: enhancedQuestionText }}
                    />
                    <UmlautButtons
                      langCode={langCode}
                      onInsertChar={insertCharIntoTextarea}
                    />
                  </div>
                )}
                <div className="mt-2 max-md:mt-0.5 relative [&_.umlaut-helper]:absolute [&_.umlaut-helper]:top-3 [&_.umlaut-helper]:right-3 [&_.umlaut-helper]:z-10 max-md:[&_.umlaut-helper]:hidden">
                  <textarea
                    ref={(el) => (textareaRefs.current[index] = el)}
                    className={textareaCls}
                    value={answers[index] || ''}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    placeholder="Write your answer here..."
                    rows={1}
                    onInput={(e) => autoResizeTextarea(e.target)}
                    tabIndex={index + 1}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && !e.shiftKey && index === questionsArray.length - 1) {
                        e.preventDefault();
                        if (submitButtonRef.current && !submitButtonRef.current.disabled) {
                          submitButtonRef.current.focus();
                        }
                      }
                    }}
                  />
                  <UmlautHelper langCode={langCode} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.button
          ref={submitButtonRef}
          type="button"
          className="py-3.5 px-7 rounded-xl border-none text-base font-semibold cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#12766d] to-[#0f5d56] text-white shadow-[0_4px_12px_rgba(18,118,109,0.3)] hover:shadow-[0_6px_16px_rgba(18,118,109,0.4)] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-gradient-to-br dark:from-[#fbbf24] dark:to-[#f59e0b] dark:text-[#1d4746] dark:shadow-[0_4px_14px_rgba(251,191,36,0.35)] dark:hover:from-[#f59e0b] dark:hover:to-[#d97706] dark:hover:shadow-[0_6px_18px_rgba(251,191,36,0.45)] dark:disabled:from-[#334155] dark:disabled:to-[#1e293b] dark:disabled:text-[#64748b] dark:disabled:shadow-none dark:disabled:opacity-70 dark:disabled:hover:transform-none max-md:w-full max-md:py-3.5 max-md:px-6 max-md:min-h-[44px] max-md:text-[15px] touch-manipulation"
          onClick={handleSubmit}
          disabled={!allAnswered}
          whileTap={{ scale: 0.96 }}
          tabIndex={questionsArray.length + 1}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default QuestionAndAnswer;

